import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useState, useMemo } from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddIcon from "@mui/icons-material/Add";
import AddSmothiee from "../../Modals/addProduct";
import AddBoxArt from "../../Modals/addBoxArt";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { useGetBoxArtListQuery, useDeleteBoxArtMutation, useUpdateBoxArtStatusMutation } from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import { showToast, showError } from "../../constants/toasts";

const ManageBoxArt = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [openModal, setOpenModal] = useState(false);
  
  // Fetch box art from API
  const { data, isLoading, error, refetch } = useGetBoxArtListQuery({
    page,
    limit,
  });

  // Delete box art mutation
  const [deleteBoxArt, { isLoading: isDeleting }] = useDeleteBoxArtMutation();
  
  // Update box art status mutation
  const [updateBoxArtStatus, { isLoading: isUpdatingStatus }] = useUpdateBoxArtStatusMutation();

  // Extract box art from API response
  const boxArts = useMemo(() => {
    if (data?.data) {
      // Handle different possible response structures
      let boxArtArray: any[] = [];
      
      if (Array.isArray(data.data)) {
        boxArtArray = data.data;
      } else if (data.data.boxArt && Array.isArray(data.data.boxArt)) {
        boxArtArray = data.data.boxArt;
      } else if (data.data.boxarts && Array.isArray(data.data.boxarts)) {
        boxArtArray = data.data.boxarts;
      } else if (data.data.box && Array.isArray(data.data.box)) {
        // If box API returns box art data
        boxArtArray = data.data.box;
      }

      if (boxArtArray.length > 0) {
        return boxArtArray.map((item: any) => ({
          id: item._id || item.id,
          name: item.name || "N/A",
          image: item.image || item.imageUrl || "/static/images/placeholder.png",
          createdDate: item.createdAt 
            ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : (item.createdDate || "N/A"),
          status: item.isActive ?? item.status ?? true,
        }));
      }
    }
    return [];
  }, [data]);

  // Filter box art based on search term
  const filteredBoxArts = useMemo(() => {
    if (!searchTerm) return boxArts;
    return boxArts.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [boxArts, searchTerm]);

  const [editBoxArt, setEditBoxArt] = useState<{ id: string; name: string; image: string } | null>(null);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditBoxArt(null);
    // Refetch data after modal closes (in case a new box art was added or updated)
    refetch();
  };

  // Handle edit button click
  const handleEditClick = (item: { id: string; name: string; image: string }) => {
    setEditBoxArt(item);
    setOpenModal(true);
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [boxArtToDelete, setBoxArtToDelete] = useState<{ id: string; name: string } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setBoxArtToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the box art
  const handleConfirmDelete = async () => {
    if (!boxArtToDelete) return;

    try {
      const response = await deleteBoxArt({ id: boxArtToDelete.id }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || `Box Art "${boxArtToDelete.name}" deleted successfully`;
      showToast(successMessage);
      
      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setBoxArtToDelete(null);
    } catch (error: any) {
      console.error("Error deleting box art:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete box art. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setBoxArtToDelete(null);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (boxArtId: string, boxArtName: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    try {
      const response = await updateBoxArtStatus({ id: boxArtId, isActive: newStatus }).unwrap();
      const successMessage = response?.message || `Box Art "${boxArtName}" ${newStatus ? "activated" : "deactivated"} successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating box art status:", error);
      const errorMessage = error?.data?.message || error?.error?.data?.message || error?.message || "Failed to update box art status. Please try again.";
      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isUpdatingStatus} />
      <div className="main_title">
        <h1>Manage Box Art</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Box Art
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditBoxArt(null);
                setOpenModal(true);
              }}
            >
              <AddIcon /> Add Box Art
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Box Art Name</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Error loading box art. Please try again.
                  </TableCell>
                </TableRow>
              ) : filteredBoxArts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {isLoading ? "Loading..." : "No box art found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredBoxArts.map((item) => (
                  <TableRow hover key={item.id}>
                    <TableCell>
                      <div className="user_block no_radius">
                        <figure>
                          <img src={item.image} alt={item.name} />
                        </figure>
                        <p><b>{item.name}</b></p>
                      </div>
                    </TableCell>
                    <TableCell>{item.createdDate}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={item.status}
                        onChange={() => item.id && handleStatusToggle(item.id, item.name, item.status)}
                        disabled={isUpdatingStatus || !item.id}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton 
                          onClick={() => {
                            if (item.id) {
                              handleEditClick({
                                id: item.id,
                                name: item.name,
                                image: item.image,
                              });
                            }
                          }}
                          disabled={!item.id}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => item.id && handleDeleteClick(item.id, item.name)}
                          disabled={isDeleting || !item.id}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <AddBoxArt
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editBoxArt={editBoxArt ? {
          _id: editBoxArt.id,
          name: editBoxArt.name,
          image: editBoxArt.image,
          imageUrl: editBoxArt.image,
        } : null}
      />
      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={boxArtToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageBoxArt;
